declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iconTag: {
      addOrReplaceIconDescription: (text: string) => ReturnType;
      setAlignmentIconTag: (alignment: 'left' | 'center' | 'right') => ReturnType;
    };
  }
}

interface IconTagOptions {}
import React from 'react';
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer } from '@tiptap/react';
import { Node } from '@tiptap/core';
import type { NodeViewProps } from '@tiptap/react';
import type { Node as ProseMirrorNode } from 'prosemirror-model';

interface IconTagNodeAttrs {
  style?: string;
  'aria-describedby'?: string;
}

export const IconTagNodeView = ({ node }: NodeViewProps) => {
  const attrs = node.attrs as IconTagNodeAttrs;
  const { style, 'aria-describedby': aria } = attrs;

  return (
    <NodeViewWrapper
      as='div'
      className='material-icons'
      aria-describedby={aria}
      contentEditable={false}
      style={style ? parseStyle(style) : undefined}
    >
      <NodeViewContent as='span' />
    </NodeViewWrapper>
  );
};

export function parseStyle(styleString: string): React.CSSProperties {
  return styleString
    .split(';')
    .filter(Boolean)
    .reduce((acc: React.CSSProperties, rule: string) => {
      const [key, value] = rule.split(':').map((s: string) => s.trim());
      if (!key || !value) return acc;
      const camelKey = key.replace(/-([a-z])/g, (_: string, c: string) => c.toUpperCase());
      (acc as Record<string, string>)[camelKey] = value;
      return acc;
    }, {});
}

export const IconTag = Node.create<IconTagOptions>({
  name: 'iconTag',

  group: 'block',
  content: 'inline*',
  isolating: true,
  selectable: true,

  addAttributes() {
    return {
      'aria-describedby': {
        parseHTML: (el) => el.getAttribute('aria-describedby'),
      },
      style: {
        parseHTML: (el) => el.getAttribute('style'),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.material-icons',
      },
      {
        tag: 'span[aria-describedby]',
        getAttrs: (el) => ({
          'aria-describedby': el.getAttribute('aria-describedby'),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        class: 'material-icons',
        'aria-describedby': HTMLAttributes['aria-describedby'],
        contenteditable: 'false',
        style: HTMLAttributes.style || '',
      },
      ['span', {}, 0],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(IconTagNodeView);
  },

  addCommands() {
    return {
      addOrReplaceIconDescription:
        (text: string) =>
        ({
          commands,
        }: {
          commands: { updateAttributes: (name: string, attrs: Record<string, unknown>) => boolean };
        }) => {
          commands.updateAttributes('iconTag', {
            'aria-describedby': text,
          });
          return true;
        },

      setAlignmentIconTag:
        (alignment: 'left' | 'center' | 'right') =>
        ({
          state,
          commands,
        }: {
          state: { doc: ProseMirrorNode; selection: { from: number } };
          commands: { updateAttributes: (name: string, attrs: Record<string, unknown>) => boolean };
        }) => {
          const node = state.doc.nodeAt(state.selection.from);
          if (!node) return false;

          const style: string = (node.attrs && node.attrs.style) || '';
          const cleaned = style
            .split(';')
            .map((s: string) => s.trim())
            .filter((s: string) => s && !s.startsWith('text-align'))
            .join('; ');

          commands.updateAttributes('iconTag', {
            style: `${cleaned}${cleaned ? '; ' : ''}text-align: ${alignment}`,
          });
          return true;
        },
    };
  },
});
